import React, { type ReactNode, useId } from 'react';

import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { getTzShort } from 'src/utils/dateUtils';

interface PlacementMetricsBandProps {
  isFetching: boolean;
  /** Shown instead of the cards, with a retry button. */
  loadError?: { message: string; onRetry: () => void };
  /** Epoch millis when the shown metrics were fetched, or null to hide the timestamp. */
  lastFetchedAt: number | null;
  children?: ReactNode;
}

/** Approved Active Placements band: the heading and a row of metric cards, or an inline error. */
const PlacementMetricsBand = ({ isFetching, loadError, lastFetchedAt, children }: PlacementMetricsBandProps) => {
  const headingId = useId();

  return (
    <Column tag="section" spacing="350" spacingInset="300 none none none" aria-labelledby={headingId}>
      <Row spacing="300" alignmentVertical="baseline">
        <Heading level={2} type="h400" id={headingId}>
          Approved Active Placements
        </Heading>
        {lastFetchedAt !== null && (
          <Text type="b200" color="secondary">
            as of {moment(lastFetchedAt).format('L LT')} {getTzShort()}
          </Text>
        )}
      </Row>
      <LoadingSpinnerWrapper showSpinner={isFetching}>
        {loadError !== undefined ? (
          <Row>
            <Alert type="error">{loadError.message}</Alert>
            <Button onClick={loadError.onRetry}>Try again</Button>
          </Row>
        ) : (
          <Row widths="fill" alignmentVertical="stretch" spacing="400">
            {children}
          </Row>
        )}
      </LoadingSpinnerWrapper>
    </Column>
  );
};

export default PlacementMetricsBand;
